'use client';
import { IRepository } from '@/types';

import * as Styled from './styled';
import { Row, Stack } from '@/ui/primatives';
import Button from '@/ui/Button';
import * as Icon from '@/ui/Icon';
import CodeBlock from './CodeBlock';
import { useAuthorization } from '@/hooks/useAuthorization';
import DropdownMenu from '@/ui/DropdownMenu';
import useClipboard from '@/hooks/useClipboard';

const CodeSection: React.FC<{
  repository: IRepository;
  title: string;
  language: string;
  code: string;
  docsLink?: string;
  docsTitle?: string;
}> = ({ repository, title, language, code, docsLink, docsTitle }) => {
  const [copiedText, copy] = useClipboard(code, {
    event: {
      name: `${title.toLowerCase()}_clone_link_copied`,
      payload: {
        repository_id: repository.id,
        repository_namespace: repository.namespace,
        repository_name: repository.name,
      },
    },
  });

  return (
    <Styled.CodeSection $copied={!!copiedText}>
      <Row alignItems="center" gap={10} justifyContent="space-between">
        <b onClick={copy}>{title}</b>
        <Row className="copy-indicator" alignItems="center" gap={5}>
          <span>{!!copiedText && 'copied'}</span>
          <Icon.Copy style={{ color: copiedText ? 'success' : 'default' }} onClick={copy} />
        </Row>
      </Row>
      <CodeBlock language={language} code={code} />
      {docsLink && (
        <a className="hint link-underline" href={docsLink} target="_blank">
          {docsTitle || title} documentation
        </a>
      )}
    </Styled.CodeSection>
  );
};

const CloneButton: React.FC<{ repository: IRepository; hideMobile?: boolean }> = ({ repository, hideMobile }) => {
  const pythonCode = `import oxen\noxen.clone("${repository.url}")`;
  const oxenCode = `oxen clone ${repository.url}`;
  const auth = useAuthorization();
  const isDisabled = auth.requiresVerification();
  return (
    <Styled.CloneButton $hideMobile={hideMobile}>
      <DropdownMenu>
        <DropdownMenu.Trigger disabled={isDisabled}>
          <Button.Black nowrap small style={{ padding: '5px 10px 5px 15px' }} as="div" disabled={isDisabled}>
            Clone <Icon.DownSmall />
          </Button.Black>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content noPadding>
          <Styled.CloneContent>
            <Stack gap={20}>
              <CodeSection
                repository={repository}
                title="Oxen"
                docsTitle="Oxen CLI"
                language="bash"
                code={oxenCode}
                docsLink="https://docs.oxen.ai/getting-started/install#command-line-tools"
              />
              <CodeSection
                repository={repository}
                title="Python"
                language="python"
                code={pythonCode}
                docsTitle="Oxen Python library"
                docsLink="https://docs.oxen.ai/getting-started/python"
              />
            </Stack>
          </Styled.CloneContent>
        </DropdownMenu.Content>
      </DropdownMenu>
    </Styled.CloneButton>
  );
};

export default CloneButton;
